import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { useSaveData } from '../../hooks/useSaveData';
import { LeaveRequest } from '../../models/leave-request';
import { MutationKeyConfig } from '../../api/api-helper';
import { InvalidateRoute } from '../../hooks/useDeleteData';
import { scheduleColumns } from './columns/schedule-grid-columns';
import { usersApiKeyStore } from '../../api/users-api-key-store';
import { SiTrackGrid } from '../../components/si-track-grid/si-track-grid';
import { LoadingIndicator } from '../../components/loading-indicator.component';
import { GuardedComponent } from '../../components/guarded-component';
import { UserPermissionName } from '../../models/user-permission-name';
import { useAuthorize } from '../../hooks/use-authorize';

interface LeaveDetailsComponent {
  open: boolean;
  leaveDetails: LeaveRequest;
  config: {
    saveConfig: MutationKeyConfig;
    invalidateRoute?: InvalidateRoute;
  };
  onClosed: () => void;
}

interface ComponentCell {
  label: string;
  value: string;
}
const ComponentCell = ({ label, value }: ComponentCell) => {
  return (
    <>
      <Typography variant="body1">{label}</Typography>
      <Typography variant="body2" style={{ paddingTop: 5 }}>
        {value}
      </Typography>
    </>
  );
};
export const LeaveDetailsComponent = ({
  open,
  leaveDetails,
  onClosed,
  config: { saveConfig, invalidateRoute },
}: LeaveDetailsComponent) => {
  const [leaveNote, setLeaveNote] = useState<string>();
  const { isLoading, isError, error, mutateAsync } = useSaveData(saveConfig, invalidateRoute);

  const handleSubmit = async (type: string) => {
    const processData = {
      leaveID: leaveDetails?.leaveID,
      leaveStatusID: type == 'Reject' ? 4 : 1,
      comments: leaveNote,
    };
    const data = await mutateAsync({ data: processData });
    onClosed();
  };

  const isRejectPermission = useAuthorize([UserPermissionName?.REJECT_LEAVE]);
  const isApprovePermission = useAuthorize([UserPermissionName?.APPROVE_LEAVE]);
  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <Dialog
      open={open}
      fullWidth={true}
      maxWidth={'lg'}
      // onClose={onClosed}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">
        <Typography variant="body1">Leave Details</Typography>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClosed}
        sx={{
          position: 'absolute',
          right: 10,
          top: 10,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Tooltip title={<Typography variant="body1">Close</Typography>}>
          <CloseIcon />
        </Tooltip>
      </IconButton>
      <DialogContent dividers className="popupDilog">
        <Grid container spacing={2}>
          <Grid item lg={2} sm={2} xl={2} xs={2}>
            <ComponentCell label="Employee" value={leaveDetails?.employeeName} />
          </Grid>
          <Grid item lg={2} sm={2} xl={2} xs={2}>
            <ComponentCell label="Service Area" value={leaveDetails?.serviceArea} />
          </Grid>
          <Grid item lg={2} sm={2} xl={2} xs={2}>
            <ComponentCell label="Leave Type" value={leaveDetails?.leaveType} />
          </Grid>
          <Grid item lg={2} sm={2} xl={2} xs={2}>
            <ComponentCell label="Start Date" value={leaveDetails?.fromDate} />
          </Grid>
          <Grid item lg={2} sm={2} xl={2} xs={2}>
            <ComponentCell label="End Date" value={leaveDetails?.toDate} />
          </Grid>
          <Grid item lg={2} sm={2} xl={2} xs={2}>
            <ComponentCell label="Status" value={leaveDetails?.leaveStatus} />
          </Grid>
          <Grid item lg={6} sm={6} xl={6} xs={6}>
            <ComponentCell label="Description" value={leaveDetails?.description} />
          </Grid>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <Divider />
          </Grid>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <SiTrackGrid
              fileTitle='leave_schedule'
              dataItemKey="workOrderID"
              columns={scheduleColumns}
              queryConfig={usersApiKeyStore.getEmployeeSchedules()}
              actionPermissions={{
                export: UserPermissionName.DOWNLOAD_GRID,
              }}
            />
          </Grid>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <LoadingIndicator isLoading={isLoading} />
            <TextField
              fullWidth
              id="leave-note"
              label="Add Note"
              variant="outlined"
              multiline
              disabled={!isRejectPermission && !isApprovePermission}
              rows={4}
              onChange={(event: any) => setLeaveNote(event?.target.value)}
              autoFocus
            />
          </Grid>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <Grid container direction="row" justifyContent="flex-end" alignItems="flex-start">
              <GuardedComponent requiredPermissions={[UserPermissionName.REJECT_LEAVE]}>
                <Button
                  onClick={() => handleSubmit('Reject')}
                  style={{ backgroundColor: isReadOnlyPermission ? '#d7dbdd' : '#FE78AF' }}
                  variant="contained"
                  disabled={isReadOnlyPermission}
                >
                  Reject
                </Button>
              </GuardedComponent>
              <GuardedComponent requiredPermissions={[UserPermissionName.APPROVE_LEAVE]}>
                <Button
                  onClick={() => handleSubmit('Approve')}
                  style={{ marginLeft: 10, backgroundColor: isReadOnlyPermission ? '#d7dbdd' : '#28B463' }}
                  variant="contained"
                  disabled={isReadOnlyPermission}
                >
                  Approve
                </Button>
              </GuardedComponent>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};
